import { useCallback, useRef, useState } from 'react';
import { collectPackage } from 'app/api/package';
import Toast from 'app/utils/Toast';
import Message from 'app/utils/Message';

// Mirrors CollectPackageLineDto on the server. A DTO failure reaches the admin only as a
// generic "Invalid body" sentence, so the client must enforce these limits itself.
const CODE_MAX_LENGTH = 30;

const CODE_REQUIRED = 'Enter the collection code for this voucher.';
const CODE_TOO_LONG = `Collection code must not exceed ${CODE_MAX_LENGTH} characters.`;
const SERIAL_REQUIRED = 'Enter the serial number for this voucher.';

const toLine = (member) => ({
  reserveVoucherId: member?.reserveVoucherId,
  voucherId: member?.voucherId,
  voucherTitle: member?.voucherTitle,
  storeName: member?.storeName,
  voucherCode: '',
  serialNumber: '',
  // One message per input, shown under that input.
  codeError: '',
  serialError: '',
});

// Which input a server error belongs under. A wrong or missing collection
// code, a missing line and a repeated voucher go under the code; serial
// problems go under the serial.
const errorKeyFor = (field) =>
  field === 'serialNumber' ? 'serialError' : 'codeError';

// Returns the lines with `codeError` / `serialError` set on each offending
// input, and whether any failed.
const validateLines = (lines) => {
  const firstLineBySerial = new Map();
  let hasError = false;

  const validated = lines.map((line, index) => {
    let codeError = '';
    let serialError = '';
    const code = line.voucherCode ?? '';
    const serial = line.serialNumber ?? '';

    if (code.length === 0) {
      codeError = CODE_REQUIRED;
    } else if (code.length > CODE_MAX_LENGTH) {
      codeError = CODE_TOO_LONG;
    }

    if (serial.trim().length === 0) {
      serialError = SERIAL_REQUIRED;
    } else if (firstLineBySerial.has(serial)) {
      // Free text with no length limit, like a regular voucher's serial.
      // Duplicates are compared exactly as typed, as the server does.
      serialError = `Same serial as line ${firstLineBySerial.get(serial)}`;
    } else {
      firstLineBySerial.set(serial, index + 1);
    }

    if (codeError || serialError) hasError = true;
    return { ...line, codeError, serialError };
  });

  return { validated, hasError };
};

// Per-voucher state is keyed on reserveVoucherId, never an index or a package id.
// The server reports a failing line by voucherId (and reserveVoucherId when it has it).
const findLineKey = (lines, entry) => {
  if (entry?.reserveVoucherId != null) {
    const byReserve = lines.find(
      (line) => line.reserveVoucherId === entry.reserveVoucherId
    );
    if (byReserve) return byReserve.reserveVoucherId;
  }
  if (entry?.voucherId != null) {
    const byVoucher = lines.find((line) => line.voucherId === entry.voucherId);
    if (byVoucher) return byVoucher.reserveVoucherId;
  }
  return null;
};

const usePackageCollect = ({ onSuccess } = {}) => {
  const [lines, setLines] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [purchaseId, setPurchaseId] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const submittingRef = useRef(false);

  const openFor = useCallback((row, phone) => {
    const members = Array.isArray(row?.vouchers) ? row.vouchers : [];
    setLines(members.map(toLine));
    setPurchaseId(row?.purchaseId ?? null);
    setPhoneNumber(phone ?? '');
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  // field is 'voucherCode' or 'serialNumber'. Editing an input clears only
  // that input's error.
  const handleLineChange = useCallback((reserveVoucherId, field, value) => {
    setLines((current) =>
      current.map((line) =>
        line.reserveVoucherId === reserveVoucherId
          ? { ...line, [field]: value, [errorKeyFor(field)]: '' }
          : line
      )
    );
  }, []);

  const handleSubmit = async () => {
    if (submittingRef.current) return;

    const { validated, hasError } = validateLines(lines);
    if (hasError) {
      setLines(validated);
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const response = await collectPackage({
        phoneNumber,
        purchaseId,
        vouchers: lines.map(({ voucherId, voucherCode, serialNumber }) => ({
          voucherId,
          voucherCode,
          serialNumber,
        })),
      });

      if (response?.responseCode === 200) {
        Toast.success(response?.message);
        if (onSuccess) onSuccess();
        setIsOpen(false);
        return;
      }

      // Every failure keeps the panel open (D-08) so staff fix only the wrong lines.
      const errors = response?.data?.errors;
      if (Array.isArray(errors)) {
        // Keyed by line, then by input, so each message lands under the
        // input it is about.
        const messagesByLine = new Map();
        errors.forEach((entry) => {
          const key = findLineKey(lines, entry);
          if (key == null) return;
          const errorKey = errorKeyFor(entry.field);
          const forLine = messagesByLine.get(key) ?? {};
          forLine[errorKey] = forLine[errorKey]
            ? `${forLine[errorKey]} ${entry.message}`
            : entry.message;
          messagesByLine.set(key, forLine);
        });
        setLines((current) =>
          current.map((line) => {
            const forLine = messagesByLine.get(line.reserveVoucherId) ?? {};
            return {
              ...line,
              codeError: forLine.codeError ?? '',
              serialError: forLine.serialError ?? '',
            };
          })
        );
        const requestLevel = errors.find((entry) => entry?.voucherId == null);
        Toast.error(requestLevel?.message || Message.Package.NOTHING_COLLECTED);
      } else {
        Toast.error(
          response?.response?.message || Message.Error.COMMON_MESSAGE
        );
      }
    } catch (error) {
      Toast.error(Message.Error.COMMON_MESSAGE);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return {
    lines,
    phoneNumber,
    isOpen,
    setIsOpen,
    isSubmitting,
    openFor,
    close,
    handleLineChange,
    handleSubmit,
  };
};

export { usePackageCollect };
export default usePackageCollect;
